import { useEffect, useState } from 'react';
import { iconClassMap } from './iconClassMap';
import { useAppSelector } from '../../store/hooks';
import { selectTenantConfig } from '../../store/tenant-config/tenant-config.slice';

const ENHANCED_ATTR = 'data-fa-enhanced';
const LAST_ICON_ATTR = 'data-fa-source-icon';
const LAST_STYLE_ATTR = 'data-fa-style';

function getIconClassFromElement(element: Element): string | undefined {
  for (const className of Array.from(element.classList)) {
    if (className.startsWith('icon-') && !className.startsWith('icon-card')) {
      return className;
    }
  }

  return undefined;
}

function decorateElement(element: Element, useRegular: boolean): void {
  const iconClass = getIconClassFromElement(element);
  if (!iconClass) return;

  const faIconClass = iconClassMap[iconClass];
  if (!faIconClass) return;

  const nextStyleClass = useRegular ? 'fa-regular' : 'fa-solid';
  const previousIconClass = element.getAttribute(LAST_ICON_ATTR);
  const previousStyleClass = element.getAttribute(LAST_STYLE_ATTR);

  const alreadyCorrect =
    element.getAttribute(ENHANCED_ATTR) === 'true' &&
    previousIconClass === iconClass &&
    previousStyleClass === nextStyleClass &&
    element.classList.contains('fa') &&
    element.classList.contains(faIconClass) &&
    element.classList.contains(nextStyleClass);

  if (alreadyCorrect) {
    return;
  }

  if (previousIconClass && iconClassMap[previousIconClass]) {
    element.classList.remove(iconClassMap[previousIconClass]);
  }

  element.classList.remove('fa-solid', 'fa-regular');
  element.classList.add('fa', faIconClass, nextStyleClass);

  element.setAttribute(ENHANCED_ATTR, 'true');
  element.setAttribute(LAST_ICON_ATTR, iconClass);
  element.setAttribute(LAST_STYLE_ATTR, nextStyleClass);
}

function decorateTree(root: ParentNode, useRegular: boolean): void {
  const elements = root.querySelectorAll('[class*="icon-"]');
  elements.forEach((element) => decorateElement(element, useRegular));
}

export function FontAwesomeIconEnhancer() {
  const tenant = useAppSelector(selectTenantConfig);
  const [useRegular, setUseRegular] = useState(false);

  useEffect(() => {
    if (tenant.layout?.name === 'Bethmann') {
      setUseRegular(true);
    } else {
      setUseRegular(false);
    }
  }, [tenant]);

  useEffect(() => {
    decorateTree(document, useRegular);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;

            decorateElement(node, useRegular);
            decorateTree(node, useRegular);
          });
        }
      }
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
    });

    return () => observer.disconnect();
  }, [useRegular]);

  return null;
}
